import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";

function FormField({ label, value, onChange, type = "text", placeholder = "", className = "", as = "input" }) {
  const Component = as === "textarea" ? Textarea : Input;
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</Label>
      <Component
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
}

function EditableTable({ title, rows, onChange, testIdPrefix }) {
  const addRow = () => {
    onChange([...rows, { ctu: "", class_code: "", quantity: "" }]);
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{title}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addRow} className="gap-1 text-xs" data-testid={`${testIdPrefix}-add-row`}>
          <Plus size={14} /> Add Row
        </Button>
      </div>
      {rows.length > 0 ? (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">CTU</th>
                <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Class Code</th>
                <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Quantity</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`${testIdPrefix}-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <Input
                      value={row.ctu || ""}
                      onChange={(e) => updateRow(i, "ctu", e.target.value)}
                      className="h-8 text-sm border-slate-200"
                      placeholder="CTU"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={row.class_code || ""}
                      onChange={(e) => updateRow(i, "class_code", e.target.value)}
                      className="h-8 text-sm border-slate-200"
                      placeholder="Class Code"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={row.quantity || ""}
                      onChange={(e) => updateRow(i, "quantity", e.target.value)}
                      className="h-8 text-sm border-slate-200"
                      placeholder="Qty"
                      type="number"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Button variant="ghost" size="sm" onClick={() => removeRow(i)} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-dashed border-slate-300 rounded-md p-4 text-center text-slate-400 text-sm">
          No rows added. Click "Add Row" to start.
        </div>
      )}
    </div>
  );
}

const QUICK_REMARKS = [
  "Labels not accessible",
  "Labels replaced due to damage",
];

export default function LabelingForm({ data, onChange, isRemoval = false }) {
  return (
    <div className="space-y-6" data-testid="labeling-form">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Header Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Reference Number" value={data.reference_number} onChange={v => onChange("reference_number", v)} />
          <AutocompleteInput
            label="Applicant"
            value={data.applicant}
            onChange={v => onChange("applicant", v)}
            placeholder="Start typing to search..."
            endpoint="applicants"
            testId="applicant-input"
          />
          <FormField label="Container ID" value={data.container_id} onChange={v => onChange("container_id", v)} placeholder="e.g., MSCU1234567" />
          <FormField label="Rail Car Number" value={data.rail_car_number} onChange={v => onChange("rail_car_number", v)} />
          <FormField label="Cargo" value={data.cargo} onChange={v => onChange("cargo", v)} />
          <FormField label="Service" value={data.service} onChange={v => onChange("service", v)} placeholder={isRemoval ? "Removal" : "Labeling"} />
          <FormField label="Site of Service" value={data.site_of_service} onChange={v => onChange("site_of_service", v)} />
          <FormField label="Location" value={data.location} onChange={v => onChange("location", v)} />
          <FormField label="Date of Issuance" value={data.date_of_issuance} onChange={v => onChange("date_of_issuance", v)} type="date" />
          <AutocompleteInput
            label="Surveyor"
            value={data.surveyor}
            onChange={v => onChange("surveyor", v)}
            placeholder="Start typing to search..."
            endpoint="surveyors"
            testId="surveyor-input"
          />
          <FormField label="Date of Nomination" value={data.date_of_nomination} onChange={v => onChange("date_of_nomination", v)} type="date" />
          <FormField label="Date of Service" value={data.date_of_service} onChange={v => onChange("date_of_service", v)} type="date" />
          <FormField label="Completion Time" value={data.completion_time} onChange={v => onChange("completion_time", v)} placeholder="e.g., 14:30" />
        </div>
      </div>

      {/* Tables */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Operations Tables
        </h3>

        <EditableTable
          title="Removal Table"
          rows={data.removal_table || []}
          onChange={v => onChange("removal_table", v)}
          testIdPrefix="removal"
        />

        {!isRemoval && (
          <EditableTable
            title="Affixing Table"
            rows={data.affixing_table || []}
            onChange={v => onChange("affixing_table", v)}
            testIdPrefix="affixing"
          />
        )}
      </div>

      {/* Remarks */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Remarks
        </h3>
        <FormField
          label="Remarks"
          value={data.remarks}
          onChange={v => onChange("remarks", v)}
          as="textarea"
          placeholder="Enter any additional remarks..."
        />
        <div className="mt-3 flex gap-2 flex-wrap">
          <span className="text-xs text-slate-500 mr-1">Quick add:</span>
          {QUICK_REMARKS.map(remark => (
            <Button
              key={remark}
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => {
                const current = data.remarks || "";
                const newRemarks = current ? `${current}\n${remark}` : remark;
                onChange("remarks", newRemarks);
              }}
              data-testid={`quick-remark-${remark.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {remark}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
